import { Initiative, NumberOfHomes } from '@schema/initiative'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import { match } from 'ts-pattern'
import { EditContainer } from './blocks/shared/EditContainer'
import { SearchingForMembersEdit } from './blocks/membersParameters/SearchingForMembersEdit'
import { LocalityFieldEdit } from './blocks/membersParameters/LocalityFieldEdit'
import { MemberSearchDescriptionEdit } from './blocks/membersParameters/MemberSearchDescriptionEdit'
import { NumberOfHomesFieldEdit } from './blocks/membersParameters/NumberOfHomesFieldEdit'
import { ProjectSizeFieldEdit } from './blocks/membersParameters/ProjectSizeFieldEdit'
import { RegionFieldEdit } from './blocks/membersParameters/RegionFieldEdit'

export type MembersParametersEditProps = {
  initiative: Partial<Initiative>
}

export const MembersParametersEdit = ({
  initiative,
}: MembersParametersEditProps) => {
  const translationsRoot = useTranslations().app
  const commonTranslations = translationsRoot.common
  const creationTranslations = translationsRoot.dashboard.initiative.creation
  const emptyLabel = commonTranslations.notSpecified

  const lookingDisplay = match(initiative.searchingForMembers)
    .with(true, () => commonTranslations.yes)
    .with(false, () => commonTranslations.no)
    .otherwise(() => emptyLabel)

  const numberOfHomesLabel = (value: NumberOfHomes | null | undefined) =>
    value
      ? (creationTranslations.numberOfHomesOptions[value] ?? emptyLabel)
      : emptyLabel

  if (!initiative.id) {
    return null
  }

  const id = initiative.id

  return (
    <Column alignItems="stretch" style={{ flexGrow: 0 }} gap={'2rem'}>
      <EditContainer title={creationTranslations.areYouLookingForMembers}>
        <EditContainer.View>
          <Text.Medium color="var(--palette-blue-500)">
            {lookingDisplay}
          </Text.Medium>
        </EditContainer.View>
        <EditContainer.Edit>
          <SearchingForMembersEdit
            initiativeId={id}
            searchingForMembers={initiative.searchingForMembers ?? false}
          />
        </EditContainer.Edit>
      </EditContainer>
      {initiative.searchingForMembers && (
        <EditContainer title={creationTranslations.lookingForMembersTitle}>
          <EditContainer.View>
            <Text.Medium
              color="var(--palette-blue-500)"
              style={{ whiteSpace: 'pre-line' }}
            >
              {(initiative.searchingForMembersDescription?.trim() ?? '') !==
              '' ? (
                <ParagraphedText>
                  {initiative.searchingForMembersDescription ?? ''}
                </ParagraphedText>
              ) : (
                emptyLabel
              )}
            </Text.Medium>
          </EditContainer.View>
          <EditContainer.Edit>
            <MemberSearchDescriptionEdit
              initiativeId={id}
              searchingForMembersDescription={
                initiative.searchingForMembersDescription ?? ''
              }
            />
          </EditContainer.Edit>
        </EditContainer>
      )}
      <EditContainer title={creationTranslations.numberOfHomes}>
        <EditContainer.View>
          <Text.Medium color="var(--palette-blue-500)">
            {numberOfHomesLabel(initiative.numberOfHomes)}
          </Text.Medium>
        </EditContainer.View>
        <EditContainer.Edit>
          <NumberOfHomesFieldEdit
            initiativeId={id}
            numberOfHomes={initiative.numberOfHomes ?? null}
          />
        </EditContainer.Edit>
      </EditContainer>
      <EditContainer title={creationTranslations.projectSize}>
        <EditContainer.View>
          <Text.Medium color="var(--palette-blue-500)">
            {initiative.projectSize?.trim() !== ''
              ? (initiative.projectSize ?? '')
              : emptyLabel}
          </Text.Medium>
        </EditContainer.View>
        <EditContainer.Edit>
          <ProjectSizeFieldEdit
            initiativeId={id}
            projectSize={initiative.projectSize ?? ''}
          />
        </EditContainer.Edit>
      </EditContainer>
      <EditContainer title={commonTranslations.region}>
        <EditContainer.View>
          <Text.Medium color="var(--palette-blue-500)">
            {initiative.location?.address?.region
              ? (initiative.location?.address?.region?.name ?? '')
              : emptyLabel}
          </Text.Medium>
        </EditContainer.View>
        <EditContainer.Edit>
          <RegionFieldEdit initiative={initiative} initiativeId={id} />
        </EditContainer.Edit>
      </EditContainer>
      <EditContainer title={commonTranslations.location}>
        <EditContainer.View>
          <Text.Medium color="var(--palette-blue-500)">
            {initiative.location?.address?.locality
              ? (initiative.location?.address?.locality?.name ?? '')
              : emptyLabel}
          </Text.Medium>
        </EditContainer.View>
        <EditContainer.Edit>
          <LocalityFieldEdit initiative={initiative} initiativeId={id} />
        </EditContainer.Edit>
      </EditContainer>
    </Column>
  )
}
